import { useQuery } from "@tanstack/react-query";

import {
  ageOptions,
  genreOptions,
  idiomaOptions,
  userLevelOptions,
  subjectOptions,
  bookTypeOptions,
} from "../../../../../shared/data/nuevo-libro-autocomplete-options";
import { paisesAutocomplete } from "../../../../../shared/data/paises";
import { fetchTags } from "../../../../../shared/fetchers/tags";
import { fetchColecciones } from "../../../../../shared/fetchers/colecciones";
import { fetchEditoriales } from "../../../../../shared/fetchers/editoriales";

/**
 * Espejo en React del registro de filtros de
 * public_html/admin/recomendacion/lib/filtros.php. Agregar un filtro nuevo implica
 * agregar una entrada aquí y su constructor de SQL allá.
 */

const comoOpciones = (valores) =>
  valores.map((valor) => ({ value: valor, label: valor }));

// Books.edad es la edad mínima como entero; el catálogo la muestra como "3+".
const edadOpciones = ageOptions.map((opcion) => ({
  value: String(parseInt(opcion.value, 10)),
  label: opcion.value,
}));

const opcionesBooleanas = (etiquetaSi, etiquetaNo) => [
  { value: "1", label: etiquetaSi },
  { value: "0", label: etiquetaNo },
];

export const FILTROS = [
  {
    campo: "edad",
    label: "Edad",
    control: "multiselect",
    opciones: { tipo: "local", valores: edadOpciones },
  },
  {
    campo: "genero",
    label: "Género",
    control: "multiselect",
    opciones: {
      tipo: "local",
      valores: genreOptions.map((o) => ({ value: o.value, label: o.value })),
    },
  },
  {
    campo: "editorial",
    label: "Editorial",
    control: "multiselect",
    opciones: { tipo: "api", fuente: "editorial" },
  },
  {
    campo: "tags",
    label: "Tags",
    control: "multiselect",
    opciones: { tipo: "api", fuente: "tags" },
  },
  {
    campo: "idioma",
    label: "Idioma",
    control: "multiselect",
    opciones: { tipo: "local", valores: comoOpciones(idiomaOptions) },
  },
  {
    campo: "guia_lectura",
    label: "Guía de lectura",
    control: "boolean",
    opciones: {
      tipo: "local",
      valores: opcionesBooleanas("Tiene guía de lectura", "No tiene guía de lectura"),
    },
  },
  {
    campo: "interactivo",
    label: "Interactivo",
    control: "boolean",
    opciones: {
      tipo: "local",
      valores: opcionesBooleanas("Es interactivo", "No es interactivo"),
    },
  },
  {
    campo: "coleccion",
    label: "Colección",
    control: "multiselect",
    opciones: { tipo: "api", fuente: "coleccion" },
  },
  {
    campo: "pais_editorial",
    label: "País de la editorial",
    control: "multiselect",
    opciones: { tipo: "api", fuente: "pais_editorial" },
  },
  {
    campo: "territorio",
    label: "Territorio de disponibilidad",
    control: "multiselect",
    opciones: { tipo: "local", valores: comoOpciones(paisesAutocomplete) },
  },
  {
    campo: "nivel",
    label: "Nivel de lectura",
    control: "multiselect",
    opciones: {
      tipo: "local",
      valores: userLevelOptions.map((o) => ({ value: o.value, label: o.value })),
    },
  },
  {
    campo: "materia",
    label: "Materia",
    control: "multiselect",
    opciones: { tipo: "local", valores: comoOpciones(subjectOptions) },
  },
  {
    campo: "tipo_libro",
    label: "Tipo de libro",
    control: "multiselect",
    opciones: { tipo: "local", valores: comoOpciones(bookTypeOptions) },
  },
];

export const getDescriptor = (campo) =>
  FILTROS.find((filtro) => filtro.campo === campo);

export const FUENTES_BLOQUE = [
  { value: "mas_leidos_cliente", label: "Más leídos del cliente" },
  { value: "mas_leidos_global", label: "Más leídos global" },
  { value: "novedades", label: "Novedades (últimos cargados)" },
  { value: "catalogo", label: "Catálogo (aleatorio)" },
];

/** Las fuentes de catálogo aleatorio no admiten rango de fechas. */
export const FUENTES_CON_FECHA = [
  "mas_leidos_cliente",
  "mas_leidos_global",
  "novedades",
];

export const etiquetaFuente = (fuente) =>
  FUENTES_BLOQUE.find((f) => f.value === fuente)?.label ?? fuente;

/**
 * Carga las opciones que vienen de la API y las deja en el mismo formato
 * `{ value, label }` que las locales.
 */
export const useOpcionesFiltros = () => {
  const tagsQuery = useQuery(["tags"], fetchTags);
  const coleccionesQuery = useQuery(["colecciones"], fetchColecciones);
  const editorialesQuery = useQuery(["editoriales"], fetchEditoriales);

  const tags = (tagsQuery.data?.data ?? [])
    .map((tag) => tag?.tag)
    .filter((tag) => typeof tag === "string" && tag.length > 0);

  const colecciones = (coleccionesQuery.data?.data ?? [])
    .map((coleccion) => coleccion?.collectionName)
    .filter((nombre) => typeof nombre === "string" && nombre.length > 0);

  const editoriales = editorialesQuery.data ?? [];
  const nombresEditoriales = editoriales
    .map((editorial) => editorial?.nombre)
    .filter((nombre) => typeof nombre === "string" && nombre.length > 0);

  // Los países de editorial se derivan del mismo listado para no pedir otro endpoint.
  const paisesEditorial = [
    ...new Set(
      editoriales
        .map((editorial) => editorial?.paisEditorial)
        .filter((pais) => typeof pais === "string" && pais.length > 0)
    ),
  ];

  return {
    cargando:
      tagsQuery.isLoading ||
      coleccionesQuery.isLoading ||
      editorialesQuery.isLoading,
    opciones: {
      tags: comoOpciones([...new Set(tags)].sort()),
      coleccion: comoOpciones([...new Set(colecciones)].sort()),
      editorial: comoOpciones([...new Set(nombresEditoriales)].sort()),
      pais_editorial: comoOpciones(paisesEditorial.sort()),
    },
  };
};

/** Opciones a mostrar para un campo, resolviendo si son locales o de la API. */
export const opcionesDeCampo = (descriptor, opcionesApi = {}) => {
  if (!descriptor) return [];
  if (descriptor.opciones.tipo === "local") return descriptor.opciones.valores;
  return opcionesApi[descriptor.opciones.fuente] ?? [];
};

const etiquetaValor = (descriptor, valor, opcionesApi) => {
  const opcion = opcionesDeCampo(descriptor, opcionesApi).find(
    (o) => o.value === valor
  );
  return opcion?.label ?? valor;
};

/**
 * Describe una condición en lenguaje natural, para que el usuario pueda verificar
 * lo que armó antes de generar.
 */
export const describirCondicion = (condicion, opcionesApi = {}) => {
  const descriptor = getDescriptor(condicion?.campo);
  if (!descriptor) return "";

  const valores = condicion?.valores ?? [];
  if (valores.length === 0) return `${descriptor.label} (sin valores)`;

  if (descriptor.control === "boolean") {
    return etiquetaValor(descriptor, valores[0], opcionesApi);
  }

  const etiquetas = valores.map((valor) =>
    etiquetaValor(descriptor, valor, opcionesApi)
  );
  const operador = condicion?.operador === "excluir" ? "no es" : "es";

  return `${descriptor.label} ${operador} ${etiquetas.join(" o ")}`;
};

/** Texto de un grupo de condiciones combinadas, que siempre se unen con Y. */
export const describirGrupo = (condiciones = [], opcionesApi = {}) =>
  condiciones
    .map((condicion) => describirCondicion(condicion, opcionesApi))
    .filter((texto) => texto.length > 0)
    .join(" Y ");

export const condicionVacia = () => ({
  campo: FILTROS[0].campo,
  operador: "incluir",
  valores: [],
});
